/**
 * Expenses: list with category/supplier filters, fast-entry form with receipt upload,
 * detail with evidence and change history.
 */
import {
  EXPENSE_TREATMENTS,
  HMRC_EXPENSE_CATEGORIES,
  NOT_TAX_ADVICE,
  PAYMENT_METHODS,
} from "@shared/constants.ts";
import { expenseSchema, periodOrder } from "@shared/schemas/finance.ts";
import { useState } from "react";
import { Alert, Card, Col, Row } from "react-bootstrap";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Attachments } from "../../components/Attachments.tsx";
import { AuditHistory } from "../../components/AuditHistory.tsx";
import { AttachOnSave, uploadAll } from "../../components/forms/AttachOnSave.tsx";
import { EntityForm } from "../../components/forms/EntityForm.tsx";
import {
  DateField,
  MoneyField,
  SelectField,
  TextAreaField,
  TextField,
} from "../../components/forms/fields.tsx";
import { type Column, DataTable, Pagination } from "../../components/ui/DataTable.tsx";
import { DateRangePicker } from "../../components/ui/DateRangePicker.tsx";
import { FilterBar, FilterSelect, SearchInput } from "../../components/ui/Filters.tsx";
import { Icon } from "../../components/ui/Icon.tsx";
import {
  Can,
  ConfirmButton,
  DefinitionList,
  EmptyState,
  IconButtonLink,
  Money,
  PageHeader,
  QueryState,
  Section,
} from "../../components/ui/kit.tsx";
import { api, type Params, withQuery } from "../../lib/api.ts";
import { useFormat } from "../../lib/format.ts";
import { toOptions, useLookups } from "../../lib/lookups.ts";
import { useApi, useInvalidateAll } from "../../lib/query.ts";
import { useDateRange, useSortState, useUrlState } from "../../lib/url-state.ts";

export interface ExpenseRow {
  id: string;
  propertyId: string | null;
  ownerId: string | null;
  categoryId: string;
  categoryName: string;
  hmrcCategory: string | null;
  treatment: string;
  treatmentOverride: string | null;
  amountPence: number;
  paidDate: string;
  periodFromDate: string | null;
  periodToDate: string | null;
  contractorId: string | null;
  contractorName: string | null;
  maintenanceId: string | null;
  description: string;
  reference: string;
  paymentMethod: string | null;
  notes: string;
  addressLine1: string | null;
  postcode: string | null;
  ownerName: string | null;
  documentCount: number;
}

interface Paged<T> {
  items: T[];
  total: number;
  totalPence: number;
  page: number;
  pageSize: number;
}

export function ExpenseTable({ filters, showProperty = true }: { filters: Params; showProperty?: boolean }) {
  const f = useFormat();
  const { sort, onSort, page, setPage } = useSortState("date", "desc");
  const q = useApi<Paged<ExpenseRow>>(
    "/api/expenses",
    { ...filters, sort: sort.sort, dir: sort.dir, page },
    { keepPrevious: true },
  );
  const columns: Column<ExpenseRow>[] = [
    { key: "date", header: "Paid", sort: "date", cell: (r) => f.date(r.paidDate) },
    ...(showProperty
      ? [
          {
            key: "property",
            header: "Property",
            sort: "property",
            cell: (r: ExpenseRow) =>
              r.addressLine1 ?? (
                <span className="text-muted">General{r.ownerName ? ` · ${r.ownerName}` : ""}</span>
              ),
          } satisfies Column<ExpenseRow>,
        ]
      : []),
    { key: "description", header: "Description", cell: (r) => r.description },
    {
      key: "category",
      header: "Category",
      sort: "category",
      cell: (r) => (
        <>
          {r.categoryName}
          {r.treatment !== "revenue" && (
            <div className="small text-muted">{f.label(EXPENSE_TREATMENTS, r.treatment)}</div>
          )}
        </>
      ),
    },
    { key: "supplier", header: "Supplier", sort: "supplier", cell: (r) => r.contractorName ?? "—" },
    {
      key: "docs",
      header: <Icon name="paperclip" size={14} />,
      cell: (r) => (r.documentCount ? r.documentCount : ""),
    },
    {
      key: "amount",
      header: "Amount",
      sort: "amount",
      align: "end",
      cell: (r) => <Money pence={r.amountPence} kind="expense" />,
    },
  ];
  return (
    <QueryState query={q}>
      {(data) => (
        <>
          <DataTable
            rows={data.items}
            columns={columns}
            rowKey={(r) => r.id}
            rowLink={(r) => `/expenses/${r.id}`}
            sort={sort}
            onSort={onSort}
            empty={
              <EmptyState
                icon="expense"
                title="No expenses recorded"
                action={
                  <IconButtonLink
                    to={withQuery("/expenses/new", { propertyId: filters.propertyId as string })}
                    icon="plus"
                  >
                    Add expense
                  </IconButtonLink>
                }
              >
                Record costs such as repairs, insurance, agent fees and utilities, with receipts attached.
              </EmptyState>
            }
            footer={
              <tr className="fw-semibold">
                <td colSpan={columns.length - 1}>Total ({data.total} records)</td>
                <td className="text-end">
                  <Money pence={data.totalPence} kind="expense" />
                </td>
              </tr>
            }
          />
          <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPage={setPage} />
        </>
      )}
    </QueryState>
  );
}

export function ExpenseListPage() {
  const range = useDateRange();
  const { get } = useUrlState();
  const lookups = useLookups();
  const filters: Params = {
    from: range.from,
    to: range.to,
    propertyId: get("propertyId"),
    categoryId: get("categoryId"),
    contractorId: get("contractorId"),
    treatment: get("treatment"),
    q: get("q"),
  };
  return (
    <>
      <PageHeader
        title="Expenses"
        subtitle="Costs for your properties and portfolio"
        actions={
          <>
            <Can permission="expenses:export">
              <a className="btn btn-outline-secondary" href={withQuery("/api/expenses/export.csv", filters)}>
                <Icon name="download" size={16} /> Export CSV
              </a>
            </Can>
            <Can permission="expenses:write">
              <IconButtonLink to="/expenses/new" icon="plus">
                Add expense
              </IconButtonLink>
            </Can>
          </>
        }
      />
      <Card>
        <FilterBar>
          <DateRangePicker range={range} />
          <SearchInput placeholder="Search description, reference, supplier" />
          <FilterSelect
            param="propertyId"
            label="Property"
            options={[{ value: "none", label: "General / portfolio" }, ...toOptions(lookups?.properties)]}
          />
          <FilterSelect param="categoryId" label="Category" options={toOptions(lookups?.categories)} />
          <FilterSelect param="contractorId" label="Supplier" options={toOptions(lookups?.contractors)} />
          <FilterSelect param="treatment" label="Treatment" options={EXPENSE_TREATMENTS} />
        </FilterBar>
        <ExpenseTable filters={filters} />
      </Card>
    </>
  );
}

export function ExpenseFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const lookups = useLookups();
  const f = useFormat();
  const [files, setFiles] = useState<File[]>([]);
  return (
    <EntityForm
      schema={expenseSchema}
      checks={[periodOrder]}
      resource="/api/expenses"
      id={id}
      title={id ? "Edit expense" : "Add expense"}
      breadcrumbs={[{ label: "Expenses", to: "/expenses" }, { label: id ? "Edit" : "New" }]}
      defaults={{
        propertyId: params.get("propertyId") ?? "",
        ownerId: "",
        categoryId: params.get("categoryId") ?? "",
        treatmentOverride: "",
        amountPence: "",
        paidDate: new Date().toISOString().slice(0, 10),
        periodFromDate: "",
        periodToDate: "",
        contractorId: params.get("contractorId") ?? "",
        maintenanceId: params.get("maintenanceId") ?? "",
        description: "",
        reference: "",
        paymentMethod: "bank_transfer",
        notes: "",
      }}
      toForm={(r) => Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v === null ? "" : v]))}
      afterSave={async (saved) => uploadAll(files, "expense", saved.id as string, "receipt")}
      redirect={(saved) => `/expenses/${saved.id}`}
      cancelTo={id ? `/expenses/${id}` : "/expenses"}
    >
      {(form) => {
        const propertyId = form.watch("propertyId");
        const category = lookups?.categories.find((c) => c.id === form.watch("categoryId"));
        return (
          <Row>
            <Col md={6}>
              <SelectField
                name="propertyId"
                label="Property"
                options={toOptions(lookups?.properties)}
                placeholder="General / portfolio-level cost"
              />
            </Col>
            {!propertyId && (
              <Col md={6}>
                <SelectField
                  name="ownerId"
                  label="Owner (for general costs)"
                  options={toOptions(lookups?.owners)}
                  placeholder="Whole portfolio"
                  hint="Assign general costs to an owner for their tax figures."
                />
              </Col>
            )}
            <Col md={6}>
              <TextField name="description" label="Description" placeholder="e.g. Boiler service" required />
            </Col>
            <Col md={3}>
              <MoneyField name="amountPence" label="Amount" required />
            </Col>
            <Col md={3}>
              <DateField name="paidDate" label="Date paid" required />
            </Col>
            <Col md={6}>
              <SelectField
                name="categoryId"
                label="Category"
                options={toOptions(
                  lookups?.categories,
                  (c) => !c.isArchived || c.id === form.getValues("categoryId"),
                )}
                placeholder="Choose a category"
                required
                hint={
                  category
                    ? `${f.label(EXPENSE_TREATMENTS, category.treatment)}${category.hmrcCategory ? ` · HMRC: ${f.label(HMRC_EXPENSE_CATEGORIES, category.hmrcCategory)}` : ""}`
                    : undefined
                }
              />
            </Col>
            <Col md={6}>
              <SelectField
                name="contractorId"
                label="Supplier / contractor"
                options={toOptions(lookups?.contractors)}
                placeholder="None"
              />
            </Col>
            <Col md={4}>
              <SelectField
                name="paymentMethod"
                label="Payment method"
                options={PAYMENT_METHODS}
                placeholder="—"
              />
            </Col>
            <Col md={4}>
              <TextField name="reference" label="Invoice / reference" />
            </Col>
            <Col md={4}>
              <SelectField
                name="treatmentOverride"
                label="Treatment override"
                options={EXPENSE_TREATMENTS}
                placeholder="Use category default"
                hint="Only if this transaction differs from its category."
              />
            </Col>
            <Col md={6}>
              <DateField
                name="periodFromDate"
                label="Covers period from"
                hint="Optional, e.g. an annual insurance premium (accruals)."
              />
            </Col>
            <Col md={6}>
              <DateField name="periodToDate" label="Covers period to" />
            </Col>
            <Col md={12}>
              <TextAreaField name="notes" label="Notes" rows={2} />
            </Col>
            {!id && (
              <Col md={12}>
                <AttachOnSave
                  files={files}
                  onChange={setFiles}
                  label="Upload receipt or invoice (optional)"
                />
              </Col>
            )}
            <Col md={12}>
              <Alert variant="light" className="small mb-0 disclaimer">
                {NOT_TAX_ADVICE}
              </Alert>
            </Col>
          </Row>
        );
      }}
    </EntityForm>
  );
}

export function ExpenseDetailPage() {
  const { id } = useParams();
  const f = useFormat();
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  const q = useApi<ExpenseRow>(`/api/expenses/${id}`);
  return (
    <QueryState query={q}>
      {(r) => (
        <>
          <PageHeader
            title={
              <>
                <Money pence={r.amountPence} kind="expense" /> · {r.description}
              </>
            }
            subtitle={`Paid ${f.date(r.paidDate)}`}
            breadcrumbs={[{ label: "Expenses", to: "/expenses" }, { label: "Transaction" }]}
            actions={
              <>
                <Can permission="expenses:write">
                  <IconButtonLink to={`/expenses/${r.id}/edit`} icon="edit" variant="outline-primary">
                    Edit
                  </IconButtonLink>
                </Can>
                <Can permission="expenses:delete">
                  <ConfirmButton
                    size="md"
                    title="Delete this expense?"
                    body="It will be removed from totals, reports and tax figures. The change is kept in the audit history."
                    onConfirm={async () => {
                      await api.delete(`/api/expenses/${r.id}`);
                      await invalidate();
                      navigate("/expenses");
                    }}
                  >
                    Delete
                  </ConfirmButton>
                </Can>
              </>
            }
          />
          <Row>
            <Col lg={8}>
              <Section title="Transaction details">
                <DefinitionList
                  items={[
                    [
                      "Property",
                      r.propertyId ? (
                        <Link key="p" to={`/properties/${r.propertyId}`}>
                          {r.addressLine1}, {r.postcode}
                        </Link>
                      ) : (
                        `General / portfolio${r.ownerName ? ` (${r.ownerName})` : ""}`
                      ),
                    ],
                    ["Amount", <Money key="a" pence={r.amountPence} kind="expense" />],
                    ["Date paid", f.date(r.paidDate)],
                    ["Category", r.categoryName],
                    [
                      "Treatment",
                      `${f.label(EXPENSE_TREATMENTS, r.treatment)}${r.treatmentOverride ? " (overridden)" : ""}`,
                    ],
                    [
                      "HMRC category (label)",
                      r.treatment === "revenue"
                        ? f.label(HMRC_EXPENSE_CATEGORIES, r.hmrcCategory ?? "other")
                        : r.treatment === "finance_cost"
                          ? "Finance costs"
                          : "Not a periodic HMRC expense",
                    ],
                    [
                      "Supplier",
                      r.contractorId ? (
                        <Link key="c" to={`/contractors/${r.contractorId}`}>
                          {r.contractorName}
                        </Link>
                      ) : (
                        "—"
                      ),
                    ],
                    [
                      "Maintenance",
                      r.maintenanceId ? (
                        <Link key="m" to={`/maintenance/${r.maintenanceId}`}>
                          View issue
                        </Link>
                      ) : (
                        "—"
                      ),
                    ],
                    [
                      "Period covered",
                      r.periodFromDate ? `${f.date(r.periodFromDate)} – ${f.date(r.periodToDate)}` : "—",
                    ],
                    ["Payment method", f.label(PAYMENT_METHODS, r.paymentMethod)],
                    ["Reference", r.reference],
                    ["Notes", r.notes],
                  ]}
                />
              </Section>
              <AuditHistory entityType="expense" entityId={r.id} />
            </Col>
            <Col lg={4}>
              <Attachments entityType="expense" entityId={r.id} title="Receipts & invoices" />
            </Col>
          </Row>
        </>
      )}
    </QueryState>
  );
}
